import { Page, StyleSheet, Text, View } from "@react-pdf/renderer";
import {
  parsedJsonString,
  sizesWithQty,
  sortProductSizes,
  stripHtml,
} from "utils/UtilityFuncs";
import WoCuttingPdfHeader from "./WoCuttingPdfHeader";
import WoCuttingPdfInfo from "./WoCuttingPdfInfo";
import { useEffect, useState } from "react";

let cuttingStyles = StyleSheet.create({
  borderTop: { borderTop: "1px solid grey" },
  borderRight: { borderRight: "1px solid grey" },
  borderBottom: { borderBottom: "1px solid grey" },
  borderLeft: { borderLeft: "1px solid grey" },

  bgGray: { backgroundColor: "lightgray" },

  cellWidth: { width: 300 },

  cellSpacingX: { paddingLeft: 5, paddingRight: 5 },
  cellSpacingY: { paddingTop: 5, paddingBottom: 5 },

  headingMain: { fontSize: 14, fontWeight: "bold" },

  headingText: {
    fontSize: 10,
    fontWeight: "600",
  },
  valueText: {
    fontSize: 10,
  },
});

export default function WoCuttingPdfProducts({
  styles,
  locationLogo,
  logo,
  selectedWO,
  approvalColorMap,
  productsList,
  showContactShipping = true,
}) {
  /*   All States
   ********************************************* */

  /*   All Functions
   ********************************************* */
  const limitFirstPage = 3; // Limit for the first page
  const limitOtherPages = 5; // Limit for subsequent pages

  const getTotalPages = (list) => {
    if (!list || list.length === 0) {
      // No products to paginate -- still need page 1 if it's the only page
      // carrying the header / Contact & Shipping info, or PDF export ends
      // up with zero pages and downloads/opens as empty.
      return showContactShipping ? 1 : 0;
    }

    const firstPageItems = Math.min(list.length, limitFirstPage);
    const remainingItems = list.length - firstPageItems;

    return 1 + Math.ceil(remainingItems / limitOtherPages);
  };

  // Products merged per `keyOf` (the model by default), their sizes summed.
  const getCombinedISizeProducts = (
    products,
    keyOf = (product) => product.productStyle,
  ) => {
    let groupedByStyle = products.reduce((acc, product) => {
      const key = keyOf(product);
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(product);
      return acc;
    }, {});

    // console.log("groupedByStyle", groupedByStyle);

    let processedProducts = [];
    Object.keys(groupedByStyle).forEach((style) => {
      let productsForStyle = groupedByStyle[style];

      let combinedSizes = {};
      // Go through each product and parse sizes
      productsForStyle.forEach((product) => {
        let sizes = sizesWithQty(product.productSizes);
        sizes.forEach((sizeObj) => {
          if (combinedSizes[sizeObj.size]) {
            combinedSizes[sizeObj.size].quantity += parseInt(sizeObj.quantity);
            combinedSizes[sizeObj.size].cost += parseFloat(sizeObj.cost);
          } else {
            combinedSizes[sizeObj.size] = {
              ...sizeObj,
              quantity: parseInt(sizeObj.quantity),
              cost: parseFloat(sizeObj.cost),
            };
          }
        });
      });
      // Convert combinedSizes back to array. Object key order is insertion
      // order, so this inherits whatever order the first product happened to
      // store -- sortProductSizes below puts it back in the defined order.
      let combinedSizesArray = Object.keys(combinedSizes).map((sizeKey) => ({
        size: sizeKey,
        quantity: combinedSizes[sizeKey].quantity,
        price: combinedSizes[sizeKey].price,
        cost: combinedSizes[sizeKey].cost,
        id: combinedSizes[sizeKey].id,
        sortId: combinedSizes[sizeKey].sortId,
      }));
      // Create a new product entry
      let newProductEntry = {
        ...productsForStyle[0], // Take other details from the first product
        productSizes: sortProductSizes(combinedSizesArray),
      };
      processedProducts.push(newProductEntry);
    });

    return processedProducts;
  };

  const columnsPerRow = 5;

  // Sizes in rows of columnsPerRow, and their total.
  const arrangeSizes = (productSizes) => {
    let sizesArray = sizesWithQty(productSizes);
    if (sizesArray.length === 0) {
      sizesArray = [{ size: "N/A", quantity: 0 }];
    }
    let arrangedSizes = [];
    for (let i = 0; i < Math.ceil(sizesArray.length / columnsPerRow); i++) {
      arrangedSizes.push(
        sizesArray.slice(i * columnsPerRow, (i + 1) * columnsPerRow),
      );
    }
    const totalQty = sizesArray.reduce(
      (total, size) => total + (parseInt(size.quantity) || 0),
      0,
    );
    return { arrangedSizes, totalQty };
  };

  // One block per MODEL -- what pages are counted in. Counting the raw
  // products instead (one per colour) left trailing pages blank whenever a
  // model came in more than one colour (user, 2026-10-22).
  const processedProducts = productsList
    ? getCombinedISizeProducts(productsList).map((product) => ({
        id: product.id,
        fabric: product.productFabric,
        styleId: product.productStyle,
        color: product.productColor,
        description: product.productDescription,
        notes: product.notes,
        ...arrangeSizes(product.productSizes),
      }))
    : [];

  // Color Summary (user, 2026-10-22): the same sizes summed per COLOUR,
  // across every model, after the model blocks.
  const colorOf = (product) => product.productColor || "-";
  const colorSummary = productsList
    ? getCombinedISizeProducts(productsList, colorOf).map((product) => ({
        color: colorOf(product),
        models: [
          ...new Set(
            productsList
              .filter((p) => colorOf(p) === colorOf(product))
              .map((p) => p.productStyle)
              .filter(Boolean),
          ),
        ].join(", "),
        ...arrangeSizes(product.productSizes),
      }))
    : [];

  const totalPages = getTotalPages(processedProducts);

  const SizesTable = ({ arrangedSizes }) => (
    <View style={{ marginTop: 0 }}>
      {arrangedSizes.map((sizeRow, rowIndex) => (
        <View key={rowIndex} style={{ flexDirection: "row" }}>
          {sizeRow.map((size, sizeIndex) => (
            <View
              key={sizeIndex}
              style={[
                { width: `${100 / columnsPerRow}%`, alignItems: "center" },
                cuttingStyles.borderRight,
                rowIndex === arrangedSizes.length - 2
                  ? cuttingStyles.borderBottom
                  : {},
              ]}
            >
              <View style={[{ width: "100%", textAlign: "center" }]}>
                <View
                  style={[
                    cuttingStyles.bgGray,
                    cuttingStyles.cellSpacingY,
                    cuttingStyles.borderBottom,
                  ]}
                >
                  <Text style={cuttingStyles.headingText}>{size.size}</Text>
                </View>
                <View style={[cuttingStyles.cellSpacingY]}>
                  <Text style={cuttingStyles.headingText}>{size.quantity}</Text>
                </View>
              </View>
            </View>
          ))}
        </View>
      ))}
    </View>
  );

  /*   All UseEffects
   ********************************************* */
  let productStyle = 0;

  return (
    <>
      {productsList &&
        Array.from({ length: totalPages }, (_, pagesIndex) => {
          let sliceStart, sliceEnd;

          if (pagesIndex === 0) {
            sliceStart = 0;
            sliceEnd = limitFirstPage;
          } else {
            sliceStart = limitFirstPage + (pagesIndex - 1) * limitOtherPages;
            sliceEnd = sliceStart + limitOtherPages;
          }

          // console.log("processedProducts----", processedProducts);

          // return <></>;

          const itemsInPage = processedProducts.slice(sliceStart, sliceEnd);

          return (
            <Page key={pagesIndex} size="LETTER" style={styles.page}>
              {pagesIndex === 0 && (
                <WoCuttingPdfHeader
                  locationLogo={locationLogo}
                  logo={logo}
                  styles={styles}
                  selectedWO={selectedWO}
                />
              )}

              {/*   Contact and Shipping Info
               ********************************************* */}
              {pagesIndex === 0 && false && (
                <WoCuttingPdfInfo
                  styles={styles}
                  selectedWO={selectedWO}
                  productsList={productsList}
                  showContactShipping={showContactShipping}
                />
              )}
              {/*   ALL Product Details
               ********************************************* */}
              <View style={[styles.productDetails, { gap: 10, marginTop: 10 }]}>
                {/* ----------  Looping Products   ---------- */}
                {itemsInPage.map((item, index) => {
                  productStyle += 1;
                  return (
                    <View style={styles.mainContainer} key={item?.id}>
                      {/* Product Color */}
                      <View>
                        <Text
                          style={[
                            { textAlign: "center" },
                            cuttingStyles.headingMain,
                            cuttingStyles.borderBottom,
                            cuttingStyles.cellSpacingY,
                          ]}
                        >
                          Product Style : {item.styleId} - {item.description}
                        </Text>
                      </View>
                      {/* Modal Fabric Qty */}
                      <View
                        style={[
                          { flexDirection: "row" },
                          cuttingStyles.borderBottom,
                        ]}
                      >
                        {/* Style No# */}
                        <View
                          style={[
                            { flexDirection: "row", width: "33%" },
                            cuttingStyles.borderRight,
                          ]}
                        >
                          <View
                            style={[
                              cuttingStyles.borderRight,
                              cuttingStyles.cellWidth,
                            ]}
                          >
                            <Text
                              style={[
                                cuttingStyles.headingText,
                                cuttingStyles.cellSpacingX,
                                cuttingStyles.cellSpacingY,
                              ]}
                            >
                              Model Number:
                            </Text>
                          </View>
                          <View style={[cuttingStyles.cellWidth]}>
                            <Text
                              style={[
                                cuttingStyles.valueText,
                                cuttingStyles.cellSpacingX,
                                cuttingStyles.cellSpacingY,
                              ]}
                            >
                              {item.styleId}
                            </Text>
                          </View>
                        </View>
                        {/* Fabric */}
                        <View
                          style={[
                            { flexDirection: "row", width: "33%" },
                            cuttingStyles.borderRight,
                          ]}
                        >
                          <View
                            style={[
                              cuttingStyles.borderRight,
                              cuttingStyles.cellWidth,
                            ]}
                          >
                            <Text
                              style={[
                                cuttingStyles.headingText,
                                cuttingStyles.cellSpacingX,
                                cuttingStyles.cellSpacingY,
                              ]}
                            >
                              Fabric:
                            </Text>
                          </View>
                          <View style={[cuttingStyles.cellWidth]}>
                            <Text
                              style={[
                                cuttingStyles.valueText,
                                cuttingStyles.cellSpacingX,
                                cuttingStyles.cellSpacingY,
                              ]}
                            >
                              {item.fabric}
                            </Text>
                          </View>
                        </View>
                        {/* Quantity */}
                        <View style={[{ flexDirection: "row", width: "33%" }]}>
                          <View
                            style={[
                              cuttingStyles.borderRight,
                              cuttingStyles.cellWidth,
                            ]}
                          >
                            <Text
                              style={[
                                cuttingStyles.headingText,
                                cuttingStyles.cellSpacingX,
                                cuttingStyles.cellSpacingY,
                              ]}
                            >
                              Qty:
                            </Text>
                          </View>
                          <View style={[cuttingStyles.cellWidth]}>
                            <Text
                              style={[
                                cuttingStyles.valueText,
                                cuttingStyles.cellSpacingX,
                                cuttingStyles.cellSpacingY,
                              ]}
                            >
                              {item.totalQty}
                            </Text>
                          </View>
                        </View>
                      </View>
                      {/* ******************** Sizes Table ************************* */}
                      <SizesTable arrangedSizes={item.arrangedSizes} />
                      {/* ******************** Comments Notes ************************* */}
                      <Text style={[styles.prodComments, { marginTop: 0 }]}>
                        COMMENTS: {stripHtml(item?.notes)}
                      </Text>
                    </View>
                  );
                })}
              </View>

              {/*   Color Summary -- after the last model block. The page
                  wraps on its own when it does not fit; a colour block is
                  never split across pages.
               ********************************************* */}
              {pagesIndex === totalPages - 1 && colorSummary.length > 0 && (
                <View style={{ marginTop: 15 }}>
                  <Text
                    minPresenceAhead={80}
                    style={[cuttingStyles.headingMain, { marginBottom: 5 }]}
                  >
                    Color Summary
                  </Text>
                  <View style={{ gap: 10 }}>
                    {colorSummary.map((c) => (
                      <View
                        key={c.color}
                        style={styles.mainContainer}
                        wrap={false}
                      >
                        <View
                          style={[
                            { flexDirection: "row" },
                            cuttingStyles.borderBottom,
                          ]}
                        >
                          <Text
                            style={[
                              cuttingStyles.headingText,
                              cuttingStyles.cellSpacingX,
                              cuttingStyles.cellSpacingY,
                              cuttingStyles.borderRight,
                              { width: "40%" },
                            ]}
                          >
                            Color: {c.color}
                          </Text>
                          <Text
                            style={[
                              cuttingStyles.valueText,
                              cuttingStyles.cellSpacingX,
                              cuttingStyles.cellSpacingY,
                              cuttingStyles.borderRight,
                              { width: "40%" },
                            ]}
                          >
                            Models: {c.models || "-"}
                          </Text>
                          <Text
                            style={[
                              cuttingStyles.headingText,
                              cuttingStyles.cellSpacingX,
                              cuttingStyles.cellSpacingY,
                              { width: "20%" },
                            ]}
                          >
                            Qty: {c.totalQty}
                          </Text>
                        </View>
                        <SizesTable arrangedSizes={c.arrangedSizes} />
                      </View>
                    ))}
                  </View>
                </View>
              )}
            </Page>
          );
        })}
    </>
  );
}
